import { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import {
  deletePersonalAvatar,
  getPersonalAvatarPresignedUrl,
} from '@/modules/common/api/opsium-user.api';
import { FileUploadConfig } from '@/modules/common/components/organisms/file-upload-field';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { MyOpsiumUserProfile } from '@/modules/common/types/opsium-user-profile';
import {
  invalidateTeamMemberQueries,
  invalidateUserProfileQueries,
} from '@/modules/common/utils/query-invalidation';

export const USER_PROFILE_AVATAR_QUERY_KEY = ['user-profile-avatar'];
export const USER_PROFILE_QUERY_KEY = ['user-profile'];

export const useProfileAvatarConfig = (profileImageUrl: string | null): FileUploadConfig => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['authenticated.settings']);
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMemo(
    () => ({
      queryKey: USER_PROFILE_AVATAR_QUERY_KEY,
      fileUrl: profileImageUrl,
      getUploadUrl: ({ filename, fileSize }: { filename: string; fileSize: number }) =>
        getPersonalAvatarPresignedUrl({ fileName: filename, contentLength: fileSize }),
      deleteFile: deletePersonalAvatar,
      onUploadSuccess: () => {
        // Invalidate user profile so the new avatar URL is fetched
        invalidateUserProfileQueries(queryClient);
        // Invalidate team members since the current user appears there too
        invalidateTeamMemberQueries(queryClient);
        displayToastSuccess(t('toasts.avatarUploadSuccess'));
      },
      onUploadError: (error: Error) => {
        displayToastErrorFromApiWithFallback(
          error,
          'authenticated.settings:api.error.profile.avatarUploadFailed'
        );
      },
      onDeleteSuccess: () => {
        // Optimistically clear the user profile avatar field to avoid a flicker
        queryClient.setQueryData<MyOpsiumUserProfile>(USER_PROFILE_QUERY_KEY, oldData => {
          if (!oldData) return oldData;
          return { ...oldData, profileImageUrl: null };
        });
        // Invalidate team members since the current user appears there too
        invalidateTeamMemberQueries(queryClient);
        displayToastSuccess(t('toasts.avatarDeleteSuccess'));
      },
      onDeleteError: (error: Error) => {
        displayToastErrorFromApiWithFallback(
          error,
          'authenticated.settings:api.error.profile.avatarDeleteFailed'
        );
      },
    }),
    [profileImageUrl, queryClient, t, displayToastErrorFromApiWithFallback]
  );
};
